<template>
  <div
    :id="`${source}_add_phone_number_section`"
    class="flex flex-col gap-6 text-white font-medium"
  >
    <AssignmentSection
      :internal-form-data="internalFormData"
      @reset-form="resetFormState"
    />

    <CountryAreaSection
      :internal-form-data="internalFormData"
      :should-show-area-code="shouldShowAreaCode"
      :source="source"
      @area-code-change="handleAreaCodeChange"
      @country-change="handleCountryChange"
    />

    <PhoneNumberSection
      :internal-form-data="internalFormData"
      :should-show-phone-number="shouldShowPhoneNumber"
      :regenerating-number="regeneratingNumber"
      @regenerate="toPreviewPhoneNumbers"
    />
  </div>
</template>

<script setup>
import { ref, watch, onMounted, reactive, computed } from "vue";
import { message } from "ant-design-vue";
import { isPlivoProvider } from "@/src/utils/callsUtils.js";
import { assignCallTypes } from "@/src/constants/callConstants.js";
import {
  registrationSubmissionStatus,
  twillioBundleStatus,
  countryTelephonyProviderMapping,
  getCountryConfig,
  countryMapping,
  telephonyProviderGenericWrapper,
} from "@/src/constants/phoneNumberConstants.js";
import CallService from "@/src/api/CallService.js";
import AssignmentSection from "@/src/components/telephony_components/phone_number/sections/AssignmentSection.vue";
import CountryAreaSection from "@/src/components/telephony_components/phone_number/sections/CountryAreaSection.vue";
import PhoneNumberSection from "@/src/components/telephony_components/phone_number/sections/PhoneNumberSection.vue";

const props = defineProps({
  source: { type: String, required: true },
  formData: {
    type: Object,
    required: true,
    default: () => ({
      assignedTo: "user",
      userId: null,
      teamId: null,
      countryCode: "US",
      areaCode: null,
      generatedNumberDetails: {
        number: "",
        friendly_name: "",
        area: "",
      },
    }),
  },
});

const emit = defineEmits(["update:formData", "validatePurchase"]);

const internalFormData = reactive({ ...props.formData });
const regeneratingNumber = ref(false);
const submissionStatus = ref(null);

const currentCountryConfig = computed(() => {
  return getCountryConfig(internalFormData.countryCode);
});

const shouldShowAreaCode = computed(() => {
  return currentCountryConfig.value.requiresAreaCode;
});

const shouldShowPhoneNumber = computed(() => {
  const config = currentCountryConfig.value;

  if (config.requiresVerification) {
    return (
      submissionStatus.value === registrationSubmissionStatus.COMPLETED &&
      internalFormData?.generatedNumberDetails?.number?.trim() !== ""
    );
  }

  if (config.requiresAreaCode) {
    return !!internalFormData.areaCode;
  }

  if (isPlivoProvider(internalFormData.countryCode)) {
    return (
      regeneratingNumber.value ||
      internalFormData?.generatedNumberDetails?.number?.trim() !== ""
    );
  }

  return internalFormData?.generatedNumberDetails?.number?.trim() !== "";
});

watch(
  () => props.formData,
  (newVal) => {
    Object.assign(internalFormData, newVal);
  },
  { deep: true },
);

watch(
  () => internalFormData,
  (newVal) => {
    emit("update:formData", { ...newVal });
    emit("validatePurchase");
  },
  { deep: true },
);

watch(
  () => [
    internalFormData.assignedTo,
    internalFormData.userId,
    internalFormData.teamId,
  ],
  () => {
    const config = currentCountryConfig.value;
    if (internalFormData.countryCode === countryMapping.IN) {
      if (hasAssigneeSelected()) {
        toPreviewPhoneNumbers();
      }
      return;
    }
    if (config.autoGenerateNumber && hasAssigneeSelected()) {
      toPreviewPhoneNumbers();
    }
  },
);

const hasAssigneeSelected = () => {
  return (
    (internalFormData.assignedTo === assignCallTypes.USER &&
      internalFormData.userId) ||
    (internalFormData.assignedTo === assignCallTypes.TEAM &&
      internalFormData.teamId)
  );
};

const handleCountryChange = async (newCountryCode, oldCountryCode) => {
  if (oldCountryCode === newCountryCode) {
    return;
  }

  const config = getCountryConfig(newCountryCode);

  resetGeneratedNumberDetails();
  internalFormData.areaCode = null;

  if (config.requiresVerification) {
    await updateBundleStatus();
    if (submissionStatus.value === registrationSubmissionStatus.COMPLETED) {
      await toPreviewPhoneNumbers();
    }
  } else if (config.autoGenerateNumber && hasAssigneeSelected()) {
    await toPreviewPhoneNumbers();
  } else if (newCountryCode === countryMapping.IN) {
    if (hasAssigneeSelected()) {
      await toPreviewPhoneNumbers();
    }
  }
};

const handleAreaCodeChange = () => {
  const config = currentCountryConfig.value;
  if (config.requiresAreaCode) {
    toPreviewPhoneNumbers();
  }
};

const resetFormState = async () => {
  try {
    if (internalFormData.assignedTo === assignCallTypes.USER) {
      internalFormData.teamId = null;
    } else if (internalFormData.assignedTo === assignCallTypes.TEAM) {
      internalFormData.userId = null;
    }

    internalFormData.areaCode = null;
    resetGeneratedNumberDetails();

    const config = currentCountryConfig.value;
    if (config.autoGenerateNumber) {
      setTimeout(() => {
        if (hasAssigneeSelected()) {
          toPreviewPhoneNumbers();
        }
      }, 0);
    } else if (internalFormData.countryCode === countryMapping.IN) {
      setTimeout(() => {
        if (hasAssigneeSelected()) {
          toPreviewPhoneNumbers();
        }
      }, 0);
    }

    return Promise.resolve();
  } catch (error) {
    console.error("An error occurred while resetting form state: ", error);
    return Promise.reject(error);
  }
};

const resetGeneratedNumberDetails = () => {
  internalFormData.generatedNumberDetails = {
    number: "",
    friendly_name: "",
    area: "",
  };
};

const updateBundleStatus = async () => {
  try {
    const params = {
      country_code: internalFormData.countryCode,
    };

    const response = await CallService.getBundleStatus(params);
    if (response?.success) {
      const bundleStatus =
        response?.bundle_status ||
        registrationSubmissionStatus.BUSINESS_INFORMATION;
      const bundlesInfoStatus =
        response?.bundles_info?.[0]?.status ?? "unknown";

      submissionStatus.value = bundleStatus;

      if (
        bundleStatus === registrationSubmissionStatus.SUBMITTED &&
        (bundlesInfoStatus === twillioBundleStatus.TWILIO_APPROVED ||
          bundlesInfoStatus === twillioBundleStatus.PROVISIONALLY_APPROVED)
      ) {
        submissionStatus.value = registrationSubmissionStatus.COMPLETED;
      }
    }
    return Promise.resolve(submissionStatus.value);
  } catch (error) {
    console.error(
      "An error occurred while checking submission status: ",
      error,
    );
    return Promise.reject(error);
  }
};

const toPreviewPhoneNumbers = async () => {
  try {
    regeneratingNumber.value = true;
    const config = currentCountryConfig.value;
    let areaCode = internalFormData.areaCode;

    if (config.requiresVerification) {
      areaCode = 0;
      internalFormData.areaCode = null;
    }

    const params = {
      area_code: areaCode,
      country_code: internalFormData.countryCode,
      provider:
        telephonyProviderGenericWrapper[
          countryTelephonyProviderMapping[internalFormData.countryCode]
        ],
    };

    const response = await CallService.previewPhoneNumber(params);
    if (response?.success) {
      if (!response?.phone_number) {
        message.error(
          "No phone numbers available for this region. Please try again with another area code",
        );
        internalFormData.areaCode = null;
        return;
      }

      const data = response?.phone_number[0];
      internalFormData.generatedNumberDetails.number =
        data?.phone_number?.startsWith("+")
          ? data.phone_number
          : `+${data?.phone_number || ""}`;

      internalFormData.generatedNumberDetails.friendly_name = `${data?.friendly_name.replace(
        "-",
        " ",
      )}`;

      internalFormData.generatedNumberDetails.area = [
        data?.locality,
        data?.region,
        data?.iso_country,
      ]
        .filter(
          (item, index, array) =>
            item && item.trim() !== "" && array.indexOf(item) === index,
        )
        .join(", ");
    }
  } catch (error) {
    console.error("An error occurred while previewing phone number: ", error);
  } finally {
    regeneratingNumber.value = false;
  }
};

onMounted(async () => {
  try {
    const config = currentCountryConfig.value;
    if (config.requiresVerification) {
      await updateBundleStatus();
    } else if (config.autoGenerateNumber) {
      setTimeout(() => {
        if (hasAssigneeSelected()) {
          toPreviewPhoneNumbers();
        }
      }, 0);
    } else if (internalFormData.countryCode === countryMapping.IN) {
      setTimeout(() => {
        if (hasAssigneeSelected()) {
          toPreviewPhoneNumbers();
        }
      }, 0);
    }
  } catch (error) {
    console.error("Error initializing component:", error);
  }
});
</script>
